import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { CreditCard, X, Tag, Gift } from "lucide-react";
import { BookingPaymentDetails } from "@/hooks/useBookingPaymentDetails";

interface BookingPaymentInfoProps {
  price: number;
  canCancel: boolean;
  cancelPending: boolean;
  onCancel: () => void;
  isProviderCreated?: boolean;
  paymentDetails?: BookingPaymentDetails | null;
  isLoading?: boolean;
  hideActions?: boolean; // Hide cancel button for provider views
}

export const BookingPaymentInfo = ({
  price,
  canCancel,
  cancelPending,
  onCancel,
  isProviderCreated = false,
  paymentDetails,
  isLoading = false,
  hideActions = false
}: BookingPaymentInfoProps) => {
  const isFreeBooking = Number(price) === 0;
  
  // Calculate original price before discounts
  const totalDiscountAmount = paymentDetails?.booking_coupons?.reduce((sum, coupon) => sum + Number(coupon.discount_amount), 0) || 0;
  const originalPrice = Number(price) + totalDiscountAmount;
  
  // Helper function to format membership type display
  const formatMembershipType = (membershipType: string) => {
    switch (membershipType) {
      case 'sessions_per_month':
        return 'subscription';
      case 'pack_of_sessions':
        return 'package';
      case 'unlimited':
        return 'membership';
      default:
        return 'membership';
    }
  };
  
  return (
    <Card className="border shadow-sm bg-card">
      <CardHeader>
        <CardTitle className="text-lg flex items-center">
          <CreditCard className="h-5 w-5 mr-2 text-muted-foreground" />
          Payment Details
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Original Price (if discounts applied) */}
        {totalDiscountAmount > 0 && (
          <div className="flex justify-between items-center">
            <span className="text-muted-foreground">Original Price</span>
            <span className="text-muted-foreground line-through">£{originalPrice.toFixed(2)}</span>
          </div>
        )}

        {/* Applied Coupons */}
        {paymentDetails?.booking_coupons && paymentDetails.booking_coupons.length > 0 && (
          <div className="space-y-2">
            <span className="text-sm font-medium text-muted-foreground flex items-center">
              <Tag className="h-4 w-4 mr-1" />
              Applied Coupons
            </span>
            {paymentDetails.booking_coupons.map((coupon) => (
              <div key={coupon.id} className="bg-green-50 border border-green-200 rounded-lg p-3">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-medium text-green-800">{coupon.user_coupon.coupon.name}</p>
                    <p className="text-xs text-green-600">Code: {coupon.user_coupon.coupon.code}</p>
                  </div>
                  <Badge variant="secondary" className="bg-green-100 text-green-700">
                    -£{Number(coupon.discount_amount).toFixed(2)}
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Membership Usage */}
        {paymentDetails?.membership_info && (
          <div className="space-y-2">
            <span className="text-sm font-medium text-muted-foreground flex items-center">
              <Gift className="h-4 w-4 mr-1" />
              Membership Used
            </span>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <div className="flex justify-between items-start">
                <div>
                  {paymentDetails.membership_info.membership ? (
                    <>
                      <p className="font-medium text-blue-800">{paymentDetails.membership_info.membership.name}</p>
                      <p className="text-xs text-blue-600 capitalize">
                        {formatMembershipType(paymentDetails.membership_info.membership.membership_type)}
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="font-medium text-blue-800">Membership Session</p>
                      <p className="text-xs text-blue-600">Session used from active membership</p>
                    </>
                  )}
                </div>
                <Badge variant="secondary" className="bg-blue-100 text-blue-700">
                  Session Used
                </Badge>
              </div>
            </div>
          </div>
        )}

        {/* Final Price */}
        <div className="flex justify-between items-center">
          <span className="text-muted-foreground">
            {totalDiscountAmount > 0 || paymentDetails?.membership_info ? 'Final Price' : 'Session Fee'}
          </span>
          <span className="text-lg font-bold text-foreground">£{Number(price).toFixed(2)}</span>
        </div>
        
        {/* Payment Status */}
        {isFreeBooking ? (
          <div className="text-sm text-green-600">
            ✓ {isProviderCreated 
              ? "Complimentary session - no payment required" 
              : paymentDetails?.membership_info 
                ? "Paid with membership - no additional charge"
                : "No payment required"
            }
          </div>
        ) : (
          <p className="text-sm text-green-600">
            ✓ Payment confirmed
          </p>
        )}

        {/* Loading State */}
        {isLoading && (
          <div className="text-sm text-muted-foreground">
            Loading payment details...
          </div>
        )}
        
        {/* Cancel Button - Hidden in provider views */}
        {canCancel && !hideActions && (
          <>
            <Separator className="my-4" />
            <Button 
              onClick={onCancel}
              disabled={cancelPending}
              variant="outline"
              className="w-full text-sm border-red-200 text-red-600 hover:bg-red-50"
              size="sm"
            >
              <X className="h-4 w-4 mr-2" />
              {cancelPending ? "Cancelling..." : "Cancel Booking"}
            </Button>
            <p className="text-xs text-muted-foreground text-center">
              Free cancellation up to 2 hours before session
            </p>
          </>
        )}
      </CardContent>
    </Card>
  );
};
